import type { EffectView } from "daedalus-renderer-phaser";
import { HEAVY_RAIN_STRIPS, LIGHT_RAIN_STRIPS, RAIN_HEAVY_PRESET, RAIN_PRESET, rainPresetId, type RainLook, type RainSlant } from "odyssey-shared";

const LIGHT_STRIDE = 3;
const HEAVY_STRIDE = 5;
const HEAD_START_MS = 650;

/**
 * The strips of one rain preset: their keys, the order they fill in so a thin rain spreads across the screen, and which are
 * on.
 */
interface Strips {
  readonly preset: string;
  readonly keys: readonly string[];
  readonly order: readonly number[];
  readonly on: boolean[];
}

/**
 * Makes the strips of one preset.
 *
 * @param preset The preset id.
 * @param count How many strips cover the screen.
 * @param stride A step coprime with `count`, so the fill order spreads.
 * @returns The strips, all off.
 */
function makeStrips(preset: string, count: number, stride: number): Strips {
  return { preset, keys: Array.from({ length: count }, (_, i) => `weather:${preset}:${i}`), order: Array.from({ length: count }, (_, k) => (k * stride) % count), on: new Array<boolean>(count).fill(false) };
}

/**
 * The rain's screen-wide strips: pooled emitters of the effects view, each a share of the camera's width, attached and
 * detached only when the wanted count crosses a strip, moved with the camera every frame, and rebuilt when the view's size or
 * the storm's slant changes.
 */
export class RainStrips {
  private readonly light = makeStrips(RAIN_PRESET.id, LIGHT_RAIN_STRIPS, LIGHT_STRIDE);
  private readonly heavy = makeStrips(RAIN_HEAVY_PRESET.id, HEAVY_RAIN_STRIPS, HEAVY_STRIDE);
  private slant: RainSlant = "none";
  private viewWidth = -1;
  private viewHeight = -1;

  /**
   * @param effects The effects view whose emitters the strips are.
   */
  public constructor(private readonly effects: EffectView) {}

  /**
   * Keeps the strips a look calls for and moves them with the camera.
   *
   * @param look The strip counts.
   * @param view The camera's world view.
   * @param slant The storm's slant, or none.
   */
  public draw(look: RainLook, view: { readonly x: number; readonly y: number; readonly width: number; readonly height: number }, slant: RainSlant): void {
    if (view.width !== this.viewWidth || view.height !== this.viewHeight || slant !== this.slant) {
      this.slant = slant;
      this.resize(view.width, view.height);
    }
    this.sync(this.light, look.lightStrips, LIGHT_RAIN_STRIPS, view);
    this.sync(this.heavy, look.heavyStrips, HEAVY_RAIN_STRIPS, view);
  }

  /**
   * Rebuilds the strips for a new view size: each strip's area is its share of the screen.
   *
   * @param width The view's width in world pixels.
   * @param height The view's height in world pixels.
   */
  private resize(width: number, height: number): void {
    for (const strips of [this.light, this.heavy]) {
      for (let index = 0; index < strips.keys.length; index += 1) {
        if (strips.on[index] === true) this.effects.detach(strips.keys[index] as string);
        strips.on[index] = false;
      }
    }
    this.viewWidth = width;
    this.viewHeight = height;
  }

  /**
   * Attaches or detaches strips until the first `want` of the fill order are on, and moves the ones that are.
   *
   * @param strips The preset's strips.
   * @param want How many should be on.
   * @param count How many strips cover the screen.
   * @param view The camera's world view.
   */
  private sync(strips: Strips, want: number, count: number, view: { readonly x: number; readonly y: number; readonly width: number; readonly height: number }): void {
    const stripWidth = this.viewWidth / count;
    for (let k = 0; k < count; k += 1) {
      const index = strips.order[k] as number;
      const key = strips.keys[index] as string;
      const x = view.x + index * stripWidth;
      if (k < want) {
        if (strips.on[index] !== true) {
          this.effects.attach(key, { preset: rainPresetId(strips.preset, this.slant), shape: { at: "area", cells: [[0, 0, stripWidth, this.viewHeight]] } }, x, view.y, { advanceMs: HEAD_START_MS });
          strips.on[index] = true;
        } else {
          this.effects.moveTo(key, x, view.y);
        }
      } else if (strips.on[index] === true) {
        this.effects.detach(key);
        strips.on[index] = false;
      }
    }
  }
}
